<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card">
      <el-form :model="query" ref="queryForm" :inline="true" v-show="showSearch">
        <el-form-item label="任务名称" prop="jobName">
          <el-input
            v-model="query.jobName"
            placeholder="请输入任务名称"
            clearable
            size="small"
            style="width: 240px"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item label="任务分组" prop="jobGroup">
          <el-select
            v-model="query.jobGroup"
            placeholder="请选择任务分组"
            clearable
            size="small"
            style="width: 240px"
          >
            <el-option label="默认" value="DEFAULT" />
            <el-option label="同步" value="SYNC" />
            <el-option label="素材" value="MATERIAL" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select
            v-model="query.status"
            placeholder="任务状态"
            clearable
            size="small"
            style="width: 240px"
          >
            <el-option label="正常" value="0" />
            <el-option label="暂停" value="1" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">搜索</el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>

      <table-operations
        @add="handleAdd"
        :show-edit="true"
        :disable-edit="single"
        @edit="handleUpdate"
        :show-delete="true"
        :disable-delete="multiple"
        @delete="handleDelete"
      >
        <template slot="right-toolbar">
          <right-toolbar :showSearch.sync="showSearch" @queryTable="fetchList"></right-toolbar>
        </template>
      </table-operations>

      <el-table v-loading="loading" :data="jobList" row-key="jobId" @selection-change="handleSelectionChange" border>
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column label="任务编号" width="80" align="center" prop="jobId" />
        <el-table-column label="任务名称" align="center" prop="jobName" :show-overflow-tooltip="true" />
        <el-table-column label="任务分组" align="center" prop="jobGroup" width="100">
          <template slot-scope="scope">
            <span v-if="scope.row.jobGroup === 'DEFAULT'">默认</span>
            <span v-else-if="scope.row.jobGroup === 'SYNC'">同步</span>
            <span v-else-if="scope.row.jobGroup === 'MATERIAL'">素材</span>
            <span v-else>{{ scope.row.jobGroup }}</span>
          </template>
        </el-table-column>
        <el-table-column label="调用方法" align="center" prop="invokeTarget" :show-overflow-tooltip="true" />
        <el-table-column label="cron表达式" align="center" prop="cronExpression" :show-overflow-tooltip="true" />
        <el-table-column label="状态" align="center" width="100">
          <template slot-scope="scope">
            <el-switch
              :key="`job-switch-${scope.row.jobId}`"
              v-model="scope.row.status"
              active-value="0"
              inactive-value="1"
              @change="handleStatusChange(scope.row)"
            ></el-switch>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" width="280" class-name="small-padding fixed-width">
          <template slot-scope="scope">
            <el-button size="mini" type="text" icon="el-icon-edit" @click="handleUpdate(scope.row)">修改</el-button>
            <el-button size="mini" type="text" icon="el-icon-delete" class="danger-text" @click="handleDelete(scope.row)">删除</el-button>
            <el-dropdown @command="(command) => handleCommand(command, scope.row)">
              <el-button size="mini" type="text" icon="el-icon-d-arrow-right">
                更多<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="handleRun" icon="el-icon-caret-right">执行一次</el-dropdown-item>
                <el-dropdown-item command="handleView" icon="el-icon-view">任务详细</el-dropdown-item>
                <el-dropdown-item command="handleJobLog" icon="el-icon-s-operation">调度日志</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>

      <pagination
        v-show="total > 0"
        :total="total"
        :page.sync="pageNo"
        :limit.sync="pageSize"
        @pagination="fetchList"
      />
    </el-card>

    <!-- 添加/修改任务对话框 -->
    <el-dialog :title="title" :visible.sync="open" width="700px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="120px">
        <el-row>
          <el-col :span="12">
            <el-form-item label="任务名称" prop="jobName">
              <el-input v-model="form.jobName" placeholder="请输入任务名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="任务分组" prop="jobGroup">
              <el-select v-model="form.jobGroup" placeholder="请选择任务分组">
                <el-option label="默认" value="DEFAULT" />
                <el-option label="同步" value="SYNC" />
                <el-option label="素材" value="MATERIAL" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="调用方法" prop="invokeTarget">
              <el-input v-model="form.invokeTarget" placeholder="请输入调用目标字符串">
                <el-button slot="append" @click="handleHelp">
                  <i class="el-icon-question"></i>
                </el-button>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="cron表达式" prop="cronExpression">
              <el-input v-model="form.cronExpression" placeholder="请输入cron执行表达式" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="执行策略" prop="misfirePolicy">
              <el-radio-group v-model="form.misfirePolicy">
                <el-radio label="DO_NOTHING">立即执行</el-radio>
                <el-radio label="FIRE_NOW">执行一次</el-radio>
                <el-radio label="IGNORE">放弃执行</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否并发" prop="concurrent">
              <el-radio-group v-model="form.concurrent">
                <el-radio :label="true">允许</el-radio>
                <el-radio :label="false">禁止</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="状态">
              <el-radio-group v-model="form.status">
                <el-radio label="0">正常</el-radio>
                <el-radio label="1">暂停</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="备注" prop="remark">
              <el-input v-model="form.remark" type="textarea" placeholder="请输入内容"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="submitForm">确 定</el-button>
        <el-button @click="cancel">取 消</el-button>
      </div>
    </el-dialog>

    <!-- 任务详细对话框 -->
    <el-dialog title="任务详细" :visible.sync="openView" width="700px" append-to-body>
      <el-form :model="form" label-width="120px">
        <el-row>
          <el-col :span="12">
            <el-form-item label="任务编号：">{{ form.jobId }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="任务名称：">{{ form.jobName }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="任务分组：">{{ form.jobGroup }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="创建时间：">{{ parseTime(form.createTime) }}</el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="调用方法：">{{ form.invokeTarget }}</el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="cron表达式：">{{ form.cronExpression }}</el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="执行策略：">
              <span v-if="form.misfirePolicy === 'DO_NOTHING'">立即执行</span>
              <span v-else-if="form.misfirePolicy === 'FIRE_NOW'">执行一次</span>
              <span v-else-if="form.misfirePolicy === 'IGNORE'">放弃执行</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否并发：">
              <span v-if="form.concurrent">允许</span>
              <span v-else>禁止</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态：">
              <el-tag v-if="form.status === '0'" type="success" size="small">正常</el-tag>
              <el-tag v-else type="info" size="small">暂停</el-tag>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="openView = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listJobs, getJob, createJob, updateJob, deleteJobs, changeJobStatus, runJob } from '@/api/system/job'
import Pagination from '@/components/Pagination'
import RightToolbar from '@/components/RightToolbar'
import TableOperations from '@/components/TableOperations'

export default {
  name: 'Job',
  components: { Pagination, RightToolbar, TableOperations },
  data() {
    return {
      // 遮罩层
      loading: true,
      // 选中数组
      ids: [],
      // 非单个禁用
      single: true,
      // 非多个禁用
      multiple: true,
      // 显示搜索条件
      showSearch: true,
      // 总条数
      total: 0,
      // 任务表格数据
      jobList: [],
      // 弹出层标题
      title: '',
      // 是否显示弹出层
      open: false,
      // 是否显示详情弹出层
      openView: false,
      // 分页参数
      pageNo: 1,
      pageSize: 10,
      // 查询参数
      query: {
        jobName: undefined,
        jobGroup: undefined,
        status: undefined
      },
      // 表单参数
      form: {},
      // 表单校验
      rules: {
        jobName: [{ required: true, message: '任务名称不能为空', trigger: 'blur' }],
        jobGroup: [{ required: true, message: '任务分组不能为空', trigger: 'blur' }],
        invokeTarget: [{ required: true, message: '调用目标不能为空', trigger: 'blur' }],
        cronExpression: [{ required: true, message: 'cron表达式不能为空', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    /** 查询定时任务列表 */
    async fetchList() {
      this.loading = true
      try {
        const params = {
          pageNo: this.pageNo,
          pageSize: this.pageSize,
          ...this.query
        }
        const response = await listJobs(params)
        const records = response.data.records || []
        this.jobList = records.map(item => ({
          ...item,
          status: item.status === undefined || item.status === null ? '1' : String(item.status)
        }))
        this.total = Number(response.data.total) || 0
      } finally {
        this.loading = false
      }
    },
    /** 搜索按钮操作 */
    handleQuery() {
      this.pageNo = 1
      this.fetchList()
    },
    /** 重置按钮操作 */
    resetQuery() {
      this.query = {
        jobName: undefined,
        jobGroup: undefined,
        status: undefined
      }
      this.handleQuery()
    },
    /** 多选框选中数据 */
    handleSelectionChange(selection) {
      this.ids = selection.map(item => item.jobId)
      this.single = selection.length !== 1
      this.multiple = !selection.length
    },
    /** 表单重置 */
    reset() {
      this.form = {
        jobId: undefined,
        jobName: undefined,
        jobGroup: 'DEFAULT',
        invokeTarget: undefined,
        cronExpression: undefined,
        misfirePolicy: 'DO_NOTHING',
        concurrent: false,
        status: '0',
        remark: undefined
      }
    },
    /** 新增按钮操作 */
    handleAdd() {
      this.reset()
      this.open = true
      this.title = '添加任务'
    },
    /** 修改按钮操作 */
    handleUpdate(row) {
      this.reset()
      const jobId = row.jobId || this.ids[0]
      getJob(jobId).then(response => {
        this.form = response.data
        this.open = true
        this.title = '修改任务'
      })
    },
    /** 提交按钮 */
    submitForm() {
      this.$refs['form'].validate(async valid => {
        if (valid) {
          if (this.form.jobId) {
            await updateJob(this.form.jobId, this.form)
            this.$message.success('修改成功')
          } else {
            await createJob(this.form)
            this.$message.success('新增成功')
          }
          this.open = false
          this.fetchList()
        }
      })
    },
    /** 取消按钮 */
    cancel() {
      this.open = false
      this.reset()
    },
    /** 删除按钮操作 */
    handleDelete(row) {
      const jobIds = row.jobId ? [row.jobId] : this.ids
      this.$confirm('是否确认删除任务编号为"' + jobIds + '"的数据项？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await deleteJobs(jobIds)
        this.fetchList()
        this.$message.success('删除成功')
      }).catch(() => {})
    },
    /** 任务状态修改 */
    handleStatusChange(row) {
      const text = row.status === '0' ? '启用' : '停用'
      this.$confirm('确认要"' + text + '""' + row.jobName + '"任务吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await changeJobStatus(row.jobId, row.status)
        this.$message.success(text + '成功')
      }).catch(() => {
        row.status = row.status === '0' ? '1' : '0'
      })
    },
    /** 执行一次 */
    handleRun(row) {
      this.$confirm('确认要立即执行一次"' + row.jobName + '"任务吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await runJob(row.jobId)
        this.$message.success('执行成功')
      }).catch(() => {})
    },
    /** 任务详细 */
    handleView(row) {
      getJob(row.jobId).then(response => {
        this.form = response.data
        this.openView = true
      })
    },
    /** 调度日志 */
    handleJobLog(row) {
      this.$router.push({ path: '/system/job-log', query: { jobId: row.jobId } })
    },
    /** 更多操作 */
    handleCommand(command, row) {
      switch (command) {
        case 'handleRun':
          this.handleRun(row)
          break
        case 'handleView':
          this.handleView(row)
          break
        case 'handleJobLog':
          this.handleJobLog(row)
          break
      }
    },
    /** 帮助 */
    handleHelp() {
      this.$alert('调用目标格式：Bean名称.方法名()<br/>示例：syncJobHandler.syncAdStructure()', '帮助', {
        dangerouslyUseHTMLString: true
      })
    },
    /** 时间格式化 */
    parseTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const y = date.getFullYear()
      const m = (date.getMonth() + 1).toString().padStart(2, '0')
      const d = date.getDate().toString().padStart(2, '0')
      const h = date.getHours().toString().padStart(2, '0')
      const min = date.getMinutes().toString().padStart(2, '0')
      const s = date.getSeconds().toString().padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}:${s}`
    }
  }
}
</script>

<style scoped>
.app-container {
  padding: 0;
  background-color: #f8f8f9;
  min-height: calc(100vh - 84px);
}
.query-card {
  border: none;
}
.danger-text {
  color: #ff4d4f !important;
}
.danger-text:hover {
  color: #ff7875 !important;
}
</style>
